/**
 * The auction section's notion of "now" (spec 016, research R8).
 *
 * Every provider query in this section is day-relative — the live window starts
 * today, the sold window ends five days back, "finished" is judged against the
 * moment — so a recorded provider answer is only replayable if the clock that
 * built the query is the clock of the day it was recorded. Recorded mode pins
 * this clock to the fixture's `recordedAt` and lets it tick from there.
 *
 * ISOMORPHIC ON PURPOSE. `auctionConfig.ts` calls this and is bundled into
 * client components (the year list, the seasonal date), so there is no
 * server-only guard here. Pinning is only ever called from server code; in the
 * browser this is `Date.now()` and nothing else.
 */

/**
 * The pin lives on `globalThis`, not in module scope: the development server
 * holds one copy of this module per bundle, and a pin set in one of them left
 * the others on real time — every replayed query then asked for the wrong day
 * (found on 2026-09-08, the day after the first recording).
 */
const CLOCK_SLOT = Symbol.for('petrolboys.apibara.clock');

type TClockState = { processStart: number; pinnedAt: number | null };

const state: TClockState = ((globalThis as typeof globalThis & { [CLOCK_SLOT]?: TClockState })[CLOCK_SLOT] ??= {
  processStart: Date.now(),
  pinnedAt: null,
});

/** Milliseconds since the epoch — real, or pinned to a fixture's day. */
export const now = (): number =>
  state.pinnedAt === null ? Date.now() : state.pinnedAt + (Date.now() - state.processStart);

export const today = (): Date => new Date(now());

/**
 * Pins the clock to `recordedAtIso` plus whatever real time has elapsed since
 * this process started. A malformed timestamp is ignored rather than trusted:
 * a wrong pin would silently make every recorded answer look finished.
 */
export const pinClock = (recordedAtIso: string): void => {
  const parsed = Date.parse(recordedAtIso);

  if (!Number.isNaN(parsed)) state.pinnedAt = parsed;
};

export const isClockPinned = (): boolean => state.pinnedAt !== null;
